/**
 * advancedGradesFlag — the ONE cross-app read/write path for
 * `lwa_advanced_grades_v1`, the internal/beta switch that re-enables the
 * Grade 10–12 "Advanced Preview" experiences.
 *
 * PRODUCT DECISION (owner, 2026-09-06): Grades 10–12 moved from "production
 * curriculum" to "Advanced Preview / High School Expansion in redesign".
 * Foundations + Grades 1–9 are the production focus. ALL Grade 10–12 content is
 * PRESERVED — nothing was deleted — but it is "preserved, not production-
 * supported yet": normal parents see the 10–12 grade cards disabled ("Advanced
 * high-school courses are being redesigned"), normal children are never placed
 * into (or routed to) a 10–12 flow, and new assessments cap placement at
 * Grade 9. See docs/grade-10-12-advanced-preview.md for the status note and the
 * rebuild backlog.
 *
 * This flag exists so INTERNAL / beta users can still inspect and exercise the
 * full 1–12 behavior deliberately: signed ON, the placement cap lifts and the
 * parent picker's 10–12 cards become selectable again.
 *
 * WHY here in ui-shared: both apps consult it — web-child caps
 * `getAssessedGrade()` / the assessment ladder, web-app gates the setup wizard's
 * grade picker — and web-app CANNOT import `@child`. Same `createSignedFlag`
 * scheme as extrasFlag et al., byte-compatible with flagGuard, so the ops hook
 * `window.__lwaRigorFlags.set('lwa_advanced_grades_v1', true)` interoperates.
 * Registered in web-child/flagGuard.ts GUARDED_FLAGS + DEFAULT_OFF_FLAGS.
 *
 * DEFAULT OFF: an absent key reads false — production users get the honest
 * Grade 1–9 scope. A bare/foreign localStorage write is IGNORED; only a
 * correctly signed `{ v:'on' }` envelope enables it. Never throws.
 */

import { createSignedFlag } from './signedFlag';

export const ADVANCED_GRADES_FLAG = 'lwa_advanced_grades_v1';

const flag = createSignedFlag(ADVANCED_GRADES_FLAG); // DEFAULT OFF

/**
 * Read the Advanced Grades (10–12) inspection flag. Default OFF. Returns true
 * ONLY for a correctly signed `{ v:'on', sig }` envelope. Never throws.
 */
export function isAdvancedGradesEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write (internal/beta use). Turning it OFF clears back to the
 * default; ON stores a signed envelope. Refuses without a live parent session.
 */
export function setAdvancedGradesEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check (mirrors
 *  extrasFlag.__setExtrasOnForTests). */
export function __setAdvancedGradesOnForTests(): void {
  flag.__setOnForTests();
}
